import React from "react";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";

const TaxSlabEditor = ({ slabs = [], onChange }) => {
  const handleAddSlab = () => {
    const newSlab = {
      min_amount: "",
      max_amount: "",
      rate_type: "PERCENT",
      rate_value: "",
      calc_base: "TAXABLE",
      priority: slabs.length,
      metadata: null,
    };
    onChange([...slabs, newSlab]);
  };

  const handleRemoveSlab = (index) => {
    const updated = slabs.filter((_, i) => i !== index);
    // Reorder priorities
    const reordered = updated.map((slab, i) => ({ ...slab, priority: i }));
    onChange(reordered);
  };

  const handleUpdateSlab = (index, field, value) => {
    const updated = [...slabs];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    onChange(updated);
  };

  const handleMoveSlab = (index, direction) => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === slabs.length - 1)
    ) {
      return;
    }

    const updated = [...slabs];
    const newIndex = direction === "up" ? index - 1 : index + 1;
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];

    // Update priorities
    const reordered = updated.map((slab, i) => ({ ...slab, priority: i }));
    onChange(reordered);
  };

  return (
    <div className="space-y-3 font-poppins">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Tax Slabs
        </h4>
        <button
          type="button"
          onClick={handleAddSlab}
          className="flex items-center gap-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors"
        >
          <Plus size={16} />
          Add Slab
        </button>
      </div>

      {slabs.length > 0 ? (
        <div className="space-y-2">
          {slabs.map((slab, index) => (
            <div
              key={index}
              className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600"
            >
              <div className="grid grid-cols-12 gap-2">
                {/* Min Amount */}
                <div className="col-span-6 md:col-span-2">
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                    Min Amount
                  </label>
                  <input
                    type="number"
                    value={slab.min_amount}
                    onChange={(e) =>
                      handleUpdateSlab(index, "min_amount", e.target.value)
                    }
                    placeholder="0"
                    className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white text-sm"
                    step="0.01"
                    min="0"
                  />
                </div>

                {/* Max Amount */}
                <div className="col-span-6 md:col-span-2">
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                    Max Amount
                  </label>
                  <input
                    type="number"
                    value={slab.max_amount}
                    onChange={(e) =>
                      handleUpdateSlab(index, "max_amount", e.target.value)
                    }
                    placeholder="∞"
                    className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white text-sm"
                    step="0.01"
                    min="0"
                  />
                </div>

                {/* Rate Type */}
                <div className="col-span-6 md:col-span-2">
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                    Rate Type
                  </label>
                  <select
                    value={slab.rate_type}
                    onChange={(e) =>
                      handleUpdateSlab(index, "rate_type", e.target.value)
                    }
                    className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white text-sm"
                  >
                    <option value="PERCENT">Percent</option>
                    <option value="FLAT">Flat</option>
                  </select>
                </div>

                {/* Rate Value */}
                <div className="col-span-6 md:col-span-2">
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                    Rate Value
                  </label>
                  <input
                    type="number"
                    value={slab.rate_value}
                    onChange={(e) =>
                      handleUpdateSlab(index, "rate_value", e.target.value)
                    }
                    placeholder="0"
                    className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white text-sm"
                    step="0.01"
                    min="0"
                  />
                </div>

                {/* Calc Base */}
                <div className="col-span-6 md:col-span-2">
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                    Calc Base
                  </label>
                  <select
                    value={slab.calc_base}
                    onChange={(e) =>
                      handleUpdateSlab(index, "calc_base", e.target.value)
                    }
                    className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white text-sm"
                  >
                    <option value="TAXABLE">Taxable</option>
                    <option value="GROSS">Gross</option>
                    <option value="BASIC">Basic</option>
                  </select>
                </div>

                {/* Actions */}
                <div className="col-span-6 md:col-span-2 flex items-end gap-1">
                  <button
                    type="button"
                    onClick={() => handleMoveSlab(index, "up")}
                    disabled={index === 0}
                    className="p-1.5 text-gray-600 hover:text-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move up"
                  >
                    <ArrowUp size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMoveSlab(index, "down")}
                    disabled={index === slabs.length - 1}
                    className="p-1.5 text-gray-600 hover:text-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move down"
                  >
                    <ArrowDown size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemoveSlab(index)}
                    className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                    title="Remove slab"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Priority Display */}
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Priority: {slab.priority}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 bg-gray-50 dark:bg-gray-700 rounded-md border border-dashed border-gray-300 dark:border-gray-600">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No tax slabs defined. Click "Add Slab" to create one.
          </p>
        </div>
      )}
    </div>
  );
};

export default TaxSlabEditor;

